/*
 * Copyright (c) 2021 - 2026 All rights reserved by Ferenc Farkas <wolferi93@gmail.com>
 */

/**
 * @file chatPreferences.js
 * Pure helpers of the caller's personal Team Chat preferences (`me.preferences`
 * from `GET /api/chat/bootstrap`). Mirrors the admin store
 * (`TeamChat/store/teamChatPreferences.js`).
 */

/**
 * Preferences with defaults applied (`meetingTranscript` is ON until saved off).
 * @param {Object} [me] Bootstrap `me` object.
 * @returns {{meetingTranscript: boolean}}
 */
export const selectChatPreferences = (me) => ({
    meetingTranscript: me?.preferences?.meetingTranscript !== false,
});

/**
 * Merge a preferences patch into a bootstrap `me` object (immutable).
 * @param {Object|null} me
 * @param {{meetingTranscript?: boolean}} patch
 * @returns {Object}
 */
export const mergeChatPreferences = (me, patch = {}) => ({
    ...(me || {}),
    preferences: {...(me?.preferences || {}), ...patch},
});

/** Reasons (from `me.autoTranscribe.reason`) under which the personal toggle cannot be changed. */
const LOCKED_REASONS = {
    store_forced: 'team_chat_my_transcript_store_forced',
    store_disabled: 'team_chat_my_transcript_store_disabled',
    module_disabled: 'team_chat_my_transcript_module_disabled',
    no_permission: 'team_chat_my_transcript_no_permission',
    meetings_not_configured: 'team_chat_my_transcript_not_configured',
    storage_not_configured: 'team_chat_my_transcript_storage_not_configured',
};

/**
 * Effective transcript state for the toggle / chip.
 * Status-aware when the backend sent `me.autoTranscribe`
 * (`{enabled, reason, ...}`); otherwise falls back to the legacy
 * `recordingDefault` + personal preference behaviour.
 * @param {{me?: Object, settings?: Object, permissions?: Object}} ctx Team chat context slice.
 * @returns {{on: boolean, locked: boolean, canRecord: boolean, reason: (string|null), captionKey: (string|null)}}
 *   `locked` = the toggle cannot be changed; `captionKey` = i18n key explaining the state (null = default hint).
 */
export const resolveTranscriptState = ({me, settings, permissions} = {}) => {
    const auto = me?.autoTranscribe && typeof me.autoTranscribe === 'object' ? me.autoTranscribe : null;
    const reason = typeof auto?.reason === 'string' ? auto.reason : null;

    if (auto && reason) {
        const lockedKey = LOCKED_REASONS[reason] || null;
        const forced = reason === 'store_forced';

        return {
            on: forced ? true : Boolean(auto.enabled),
            locked: Boolean(lockedKey),
            canRecord: Boolean(permissions?.recordMeeting),
            reason,
            captionKey: lockedKey || (reason === 'active' ? 'team_chat_my_transcript_active' : null),
        };
    }

    const locked = Boolean(settings?.recordingDefault);

    return {
        on: locked || selectChatPreferences(me).meetingTranscript,
        locked,
        canRecord: Boolean(permissions?.recordMeeting),
        reason: null,
        captionKey: locked ? LOCKED_REASONS.store_forced : null,
    };
};
